import { useCallback, useEffect, useRef, useState } from 'react';
import { callApi } from 'src/utils/apiCall';
import { parseApiDate } from 'src/utils/smartReminderRules';

/**
 * Live payment status for the "booked treatment" card on the lead-detail
 * Overview tab (contracts E/F —
 * /Users/admin/Desktop/2026-09-23-payment-link-delivery-waves.md).
 *
 * Deliberately does NOT touch the shared Redux `paymentComplete` slice used
 * by the Payments & refunds tab — this is a small, local, read-only view that
 * must not clobber that tab's state.
 *
 * Dates: the API returns DRF's UK datetime format (e.g.
 * "24-09-2026 11:43:49.914866Z" — a literal trailing Z, not a UTC marker),
 * which `new Date()`/bare `moment()` cannot parse (produces Invalid Date and
 * sorts as NaN). Use the shared `parseApiDate` (handles that format + ISO)
 * everywhere these fields are compared or displayed.
 */

// Invalid/missing dates sort as 0 (oldest) rather than NaN, which would
// otherwise corrupt Array.prototype.sort ordering.
const toSortValue = (value) => parseApiDate(value)?.valueOf() || 0;
const byCreatedAtDesc = (a, b) => toSortValue(b?.created_at) - toSortValue(a?.created_at);
const byPaidAtDesc = (a, b) => toSortValue(b?.paid_at) - toSortValue(a?.paid_at);

/**
 * Pure derivation of the card's state bucket from a treatment's payments
 * list — no network access, so it's trivially unit-testable.
 *
 * Priority when more than one bucket has entries: awaiting > expired > paid
 * > noLink > none.
 *
 * @param {Array<object>} payments - results[] from the treatment payments endpoint
 * @param {*} [appointmentId] - when given AND at least one payment carries an
 *   `appointment` id (contract: Payment gains `appointment`), scope the
 *   derivation to payments for THIS appointment specifically (plus any
 *   payment with no `appointment` set at all, so older/untagged rows for the
 *   same treatment still show) instead of every payment across the whole
 *   treatment — a treatment can have more than one appointment (e.g. a
 *   rebooked consultation), each with its own deposit. Tolerant: when no
 *   payment carries `appointment` yet (contract not live/backfilled), falls
 *   back to the previous "first appointment for the treatment" behaviour
 *   unchanged.
 */
export function derivePaymentState(payments, appointmentId) {
    const all = Array.isArray(payments) ? payments : [];
    const anyTagged = appointmentId != null && all.some((p) => p?.appointment != null);
    const list = anyTagged
        ? all.filter((p) => p?.appointment == null || String(p.appointment) === String(appointmentId))
        : all;

    const paidPayments = list.filter((p) => Boolean(p?.paid_at)).sort(byPaidAtDesc);
    const paidTotal = paidPayments.reduce((sum, p) => sum + (Number(p?.amount) || 0), 0);

    const pending = list.filter((p) => !p?.paid_at);
    const awaitingPayments = pending
        .filter((p) => Boolean(p?.checkout_summary?.has_open_checkout))
        .sort(byCreatedAtDesc);
    const expiredPayments = pending
        .filter((p) => !p?.checkout_summary?.has_open_checkout && Boolean(p?.checkout_summary?.latest_is_expired))
        .sort(byCreatedAtDesc);
    const noLinkPayments = pending
        .filter((p) => !p?.checkout_summary?.has_open_checkout && p?.checkout_summary?.latest_checkout_session_id == null)
        .sort(byCreatedAtDesc);

    let state = 'none';
    let primaryPayment = null;
    if (awaitingPayments.length > 0) {
        state = 'awaiting';
        [primaryPayment] = awaitingPayments;
    } else if (expiredPayments.length > 0) {
        state = 'expired';
        [primaryPayment] = expiredPayments;
    } else if (paidPayments.length > 0) {
        state = 'paid';
        [primaryPayment] = paidPayments;
    } else if (noLinkPayments.length > 0) {
        state = 'noLink';
        [primaryPayment] = noLinkPayments;
    }

    return {
        state,
        primaryPayment,
        paidTotal,
        paidPayments,
        awaitingPayments,
        expiredPayments,
        noLinkPayments,
    };
}

const errorMessage = (res, fallback) => {
    const detail = res?.data?.detail || res?.data?.error;
    if (typeof detail === 'string' && detail) return detail;
    return fallback;
};

export default function useTreatmentPaymentStatus(practiceId, leadId, treatmentId, appointmentId) {
    const [loading, setLoading] = useState(Boolean(practiceId && leadId && treatmentId));
    const [derived, setDerived] = useState({
        state: 'none',
        primaryPayment: null,
        paidTotal: 0,
        paidPayments: [],
        awaitingPayments: [],
        expiredPayments: [],
        noLinkPayments: [],
    });
    const [session, setSession] = useState(null);
    const [reloadToken, setReloadToken] = useState(0);
    const mountedRef = useRef(true);

    useEffect(() => () => { mountedRef.current = false; }, []);

    const refresh = useCallback(() => setReloadToken((n) => n + 1), []);

    useEffect(() => {
        let cancelled = false;
        const stillCurrent = () => !cancelled && mountedRef.current;

        if (!practiceId || !leadId || !treatmentId) {
            setLoading(false);
            return undefined;
        }
        setLoading(true);

        (async () => {
            try {
                const paymentsRes = await callApi(
                    `/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/payments/`
                );
                if (!stillCurrent()) return;
                if (!paymentsRes?.ok) {
                    // Fetch error: fall back to the static "none" card silently.
                    // eslint-disable-next-line no-console
                    console.warn('useTreatmentPaymentStatus: payments fetch failed', errorMessage(paymentsRes, ''));
                    setDerived({
                        state: 'none', primaryPayment: null, paidTotal: 0,
                        paidPayments: [], awaitingPayments: [], expiredPayments: [], noLinkPayments: [],
                    });
                    setSession(null);
                    setLoading(false);
                    return;
                }

                const results = Array.isArray(paymentsRes.data?.results) ? paymentsRes.data.results : [];
                const next = derivePaymentState(results, appointmentId);
                if (!stillCurrent()) return;
                setDerived(next);

                if ((next.state === 'awaiting' || next.state === 'expired') && next.primaryPayment?.id) {
                    const sessionsRes = await callApi(
                        `/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/payments/${next.primaryPayment.id}/checkout_sessions/`
                    );
                    if (!stillCurrent()) return;
                    if (sessionsRes?.ok) {
                        const sessions = Array.isArray(sessionsRes.data?.results) ? sessionsRes.data.results : [];
                        const targetId = next.primaryPayment?.checkout_summary?.latest_checkout_session_id;
                        const matched = (targetId != null && sessions.find((s) => String(s.id) === String(targetId))) || sessions[0] || null;
                        setSession(matched);
                    } else {
                        setSession(null);
                    }
                } else {
                    setSession(null);
                }
                setLoading(false);
            } catch (err) {
                if (!stillCurrent()) return;
                // eslint-disable-next-line no-console
                console.warn('useTreatmentPaymentStatus: unexpected error', err?.message);
                setDerived({
                    state: 'none', primaryPayment: null, paidTotal: 0,
                    paidPayments: [], awaitingPayments: [], expiredPayments: [], noLinkPayments: [],
                });
                setSession(null);
                setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [practiceId, leadId, treatmentId, appointmentId, reloadToken]);

    // Cheap refresh on tab/window focus regain — good enough per spec, no
    // websocket plumbing required.
    useEffect(() => {
        if (!practiceId || !leadId || !treatmentId) return undefined;
        const onVisible = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
                refresh();
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [practiceId, leadId, treatmentId, refresh]);

    return {
        loading,
        state: derived.state,
        primary: derived.primaryPayment,
        session,
        paidTotal: derived.paidTotal,
        awaiting: derived.awaitingPayments,
        expired: derived.expiredPayments,
        noLink: derived.noLinkPayments,
        refresh,
    };
}
